import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { firebaseAuth, firebaseConfigured } from '../lib/firebase';
import { track } from '../lib/analytics';
import { useI18n } from '../i18n';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface User {
  id: string;
  email: string;
  nickname: string;
  locale: string;
  created_at: string | null;
  is_premium?: boolean;
  premium_expires_at?: string | null;
  premium_platform?: string | null;
  is_verified?: boolean;
  auth_provider?: 'password' | 'social';
  has_played_before?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, nickname: string, referralCode?: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  logout: () => void;
  updateProfile: (data: { nickname?: string; locale?: string }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
  refreshAccessToken: () => Promise<string | null>;
  sendVerificationEmail: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { t, locale } = useI18n();
  const ae = t.auth.errors as any;
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    loading: true,
  });

  // Sayfa yüklendiğinde localStorage'dan token'ı kontrol et
  useEffect(() => {
    const token = localStorage.getItem('sudoku_token');
    const refreshToken = localStorage.getItem('sudoku_refresh_token');

    if (token) {
      // Token'la profil çek (8 sn timeout — backend geç cevap verirse sonsuza bekleme)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
        .then(res => {
          clearTimeout(timeoutId);
          if (res.ok) return res.json();
          // Access token expired — refresh dene
          if (refreshToken) {
            return fetch(`${API}/auth/refresh`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: refreshToken }),
              signal: AbortSignal.timeout(8000),
            }).then(r => {
              if (!r.ok) throw new Error(ae.refreshFailed);
              return r.json();
            }).then(data => {
              localStorage.setItem('sudoku_token', data.access_token);
              localStorage.setItem('sudoku_refresh_token', data.refresh_token);
              return data.user;
            });
          }
          throw new Error(ae.tokenInvalid);
        })
        .then(user => {
          setState({ user, token: localStorage.getItem('sudoku_token'), loading: false });
        })
        .catch(() => {
          clearTimeout(timeoutId);
          localStorage.removeItem('sudoku_token');
          localStorage.removeItem('sudoku_refresh_token');
          setState({ user: null, token: null, loading: false });
        });
    } else {
      setState({ user: null, token: null, loading: false });
    }
  }, []);

  const login = async (email: string, password: string) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || ae.loginFailed);
    }
    const data = await res.json();
    localStorage.setItem('sudoku_token', data.access_token);
    localStorage.setItem('sudoku_refresh_token', data.refresh_token);
    setState({ user: data.user, token: data.access_token, loading: false });
    track('login_completed', { method: 'password' });
  };

  const register = async (email: string, password: string, nickname: string, referralCode?: string) => {
    const res = await fetch(`${API}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, nickname, locale, ...(referralCode ? { referral_code: referralCode } : {}) }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || ae.registerFailed);
    }
    const data = await res.json();
    localStorage.setItem('sudoku_token', data.access_token);
    localStorage.setItem('sudoku_refresh_token', data.refresh_token);
    setState({ user: data.user, token: data.access_token, loading: false });
    track('signup_completed', { method: 'password' });
  };

  const loginWithSocialToken = async (idToken: string, method: 'google' | 'apple') => {
    const res = await fetch(`${API}/auth/social`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken, locale }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || ae.socialLoginFailed);
    }
    const data = await res.json();
    localStorage.setItem('sudoku_token', data.access_token);
    localStorage.setItem('sudoku_refresh_token', data.refresh_token);
    setState({ user: data.user, token: data.access_token, loading: false });
    track('login_completed', { method });
  };

  const loginWithGoogle = async () => {
    if (!firebaseConfigured || !firebaseAuth) {
      throw new Error(ae.googleUnavailable);
    }
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(firebaseAuth, provider);
    const idToken = await result.user.getIdToken();
    await loginWithSocialToken(idToken, 'google');
  };

  const loginWithApple = async () => {
    if (!firebaseConfigured || !firebaseAuth) {
      throw new Error(ae.appleUnavailable);
    }
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const result = await signInWithPopup(firebaseAuth, provider);
    const idToken = await result.user.getIdToken();
    await loginWithSocialToken(idToken, 'apple');
  };

  const refreshAccessToken = async (): Promise<string | null> => {
    const refreshToken = localStorage.getItem('sudoku_refresh_token');
    if (!refreshToken) return null;
    try {
      const r = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!r.ok) throw new Error(ae.refreshFailed);
      const data = await r.json();
      localStorage.setItem('sudoku_token', data.access_token);
      localStorage.setItem('sudoku_refresh_token', data.refresh_token);
      setState(prev => ({ ...prev, token: data.access_token }));
      return data.access_token;
    } catch {
      localStorage.removeItem('sudoku_token');
      localStorage.removeItem('sudoku_refresh_token');
      setState({ user: null, token: null, loading: false });
      return null;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const token = localStorage.getItem('sudoku_token');
    if (!token) throw new Error(ae.notAuthenticated);
    const res = await fetch(`${API}/auth/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || ae.passwordChangeFailed);
    }
  };

  const logout = () => {
    localStorage.removeItem('sudoku_token');
    localStorage.removeItem('sudoku_refresh_token');
    setState({ user: null, token: null, loading: false });
  };

  const deleteAccount = async (password: string) => {
    const token = localStorage.getItem('sudoku_token');
    if (!token) throw new Error(ae.notAuthenticated);
    const res = await fetch(`${API}/auth/me`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || ae.accountDeleteFailed);
    }
    localStorage.removeItem('sudoku_token');
    localStorage.removeItem('sudoku_refresh_token');
    setState({ user: null, token: null, loading: false });
  };

  const sendVerificationEmail = async () => {
    const token = localStorage.getItem('sudoku_token');
    if (!token) throw new Error(ae.notAuthenticated);
    const res = await fetch(`${API}/auth/send-verification`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || ae.verificationEmailFailed);
    }
  };

  const updateProfile = async (data: { nickname?: string; locale?: string }) => {
    const token = localStorage.getItem('sudoku_token');
    if (!token) throw new Error(ae.notAuthenticated);
    const res = await fetch(`${API}/auth/me`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || ae.updateFailed);
    }
    const user = await res.json();
    setState(prev => ({ ...prev, user }));
  };

  return (
    <AuthContext.Provider value={{ ...state, login, register, loginWithGoogle, loginWithApple, logout, updateProfile, changePassword, deleteAccount, refreshAccessToken, sendVerificationEmail }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
